import { addToast, Button, Modal } from '@octano/global-ui';
import { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Col, Container, Row } from 'reactstrap';
import { createStudentDocument } from '../../../api/studentDocumentsRequests';
import { useTexts } from '../hooks/useTexts';
import { StudentDocumentForm, FormData } from './StudentDocumentForm';

export interface StudentDocumentCreateModalProps {
  studentId: number;
  closeModal: (result: boolean) => void;
}

const defaultForm = {
  name: '',
  description: '',
  file: null,
};

export function StudentDocumentCreateModal({
  studentId,
  closeModal,
}: StudentDocumentCreateModalProps) {
  const { modal: texts } = useTexts();
  const forms = useForm<FormData>({ defaultValues: defaultForm });
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = forms;

  const onSubmit = useCallback(
    async (values: FormData) => {
      if (!values.file) {
        return;
      }
      const payload: any = {
        name: values.name,
        description: values.description?.trim() ?? null,
        fileId: values.file.id,
      };
      const result = await createStudentDocument(studentId, payload);
      if (result.error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: texts.error,
        });
      } else {
        addToast({
          icon: 'check',
          color: 'success',
          text: texts.success,
        });
        closeModal(true);
      }
    },
    [studentId, closeModal, texts.error, texts.success],
  );

  return (
    <Modal isOpen toggle={() => closeModal(false)} size="lg">
      <Container fluid>
        <h1 className="text-dark fs-20 mt-3 mb-4 text-center">{texts.title}</h1>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormProvider {...forms}>
            <Row>
              <StudentDocumentForm />

              <Col xs={12} className="py-4" />
              <Col xs={12} md={6}>
                <Button
                  outlined
                  text={texts.buttons.close}
                  fullwidth
                  onClick={() => closeModal(false)}
                  className="mb-3"
                />
              </Col>
              <Col xs={12} md={6}>
                <Button
                  type="submit"
                  text={texts.buttons.create}
                  className="mb-3"
                  fullwidth
                  loading={isSubmitting}
                  disabled={isSubmitting}
                />
              </Col>
            </Row>
          </FormProvider>
        </form>
      </Container>
    </Modal>
  );
}
